import React from 'react'
import { Box, Text, useResponsiveWrapperContext } from '@usap/uicomponents'
import ContentLoader from 'react-content-loader'

import PropTypes from 'prop-types'
import { Title } from '../meta'
import TextInputWithIcon from '../common/TextInputWithIcon'
import formatProp65Warning from '../../helpers/formatProp65Warning'

const Loader = () => (
  <ContentLoader
    height={32}
    width={400}
    speed={1}
    primaryColor="#f3f3f3"
    secondaryColor="#ecebeb"
  >
    <rect
      x="0"
      y="0"
      rx="3"
      ry="3"
      width="200"
      height="10"
    />
  </ContentLoader>
)

const LabelToDisplay = props => {
  const {
    isReactComponent, label, align, textSize, headerPostFix,
  } = props
  return isReactComponent
    ? label
    : (
      <Text
        textAlign={align}
        size={textSize}
        color="dark-2"
        wordBreak="break-word"
        lineHeight={1.5}
        dangerouslySetInnerHTML={{
          __html: `${label}${headerPostFix}`,
        }}
      />
    )
}
export default function Table({
  headerDirection = 'row',
  title,
  search,
  searchPlaceholder = 'search',
  data = [],
  columns = [],
  searchWidth = '25%',
  searchAction = () => {},
  padHeader = 'small',
  showHeader = true,
  border = true,
  alternateColor = false,
  textSize = 'small',
  titletype = 'h2',
  margin = 'small',
  isLoading,
}) {
  const { device } = useResponsiveWrapperContext()

  const isSmallMobile = searchPlaceholder === 'vehicle' && device === 'smallMobile'

  if (!data.length) return null
  return (
    <>
      <style>
        {`
          .tableHorizontalScroll {
            overflow: -moz-scrollbars-horizontal;

            overflow-x: scroll;
            overflow-y: hidden;
            scrollbar-width: thin;

            -ms-overflow-x: scroll;
            -ms-overflow-y: hidden;
            -ms-overflow-style: scrollbar;
          }

          .tableHorizontalScroll::-webkit-scrollbar {
              height: 8px;
              background: #ffffff;
              margin-top: 12px;
          }
          .tableHorizontalScroll::-webkit-scrollbar-thumb:horizontal {
              background: #979797;
              border-radius: 4px;
          }
        `}
      </style>

      <Box gap="medium" margin={margin}>
        <Box direction={headerDirection} justify="between" gap="small">
          <Title
            className="table-title"
            type={titletype}
            size="20px"
            label={title}
          />
          {search ? (
            <Box width={searchWidth} justify="center">
              <TextInputWithIcon
                placeholder={searchPlaceholder}
                onChangeCB={searchAction}
              />
            </Box>
          ) : null}
        </Box>
        <Box
          className={isSmallMobile ? 'tableHorizontalScroll' : ''}
          width={isSmallMobile ? '400px' : '100%'}
          overflow={{ horizontal: isSmallMobile ? 'auto' : 'hidden' }}
        >
          <Box
            border={
              border
                ? { side: 'all', color: 'light-3', size: 'xsmall' }
                : { size: '0px' }
            }
            style={{
              minWidth: isSmallMobile ? '400px' : '100%',
            }}
          >
            {showHeader ? (
              <Box
                className="table-header"
                direction="row"
                background="light-1"
                pad={padHeader}
              >
                {columns.map(({ header, width }) => (
                  <Box
                    key={header}
                    width={width || `${100 / columns.length}%`}
                    margin="xsmall"
                  >
                    <Text size={textSize} weight={500} color="dark-2">
                      {header}
                    </Text>
                  </Box>
                ))}
              </Box>
            ) : null}
            <Box className="table-body">
              {data.map((rowData, index) => (
                <Box
                  border={
                      border
                        ? {
                          side: 'bottom',
                          color: 'light-3',
                          size: 'xsmall',
                        }
                        : { size: '0px' }
                    }
                  background={
                      alternateColor && !(index % 2) ? '#eeeeee' : 'none'
                    }
                  pad={padHeader}
                  direction="row"
                  key={`${rowData.name}${index}`}
                  justify="between"
                >
                  {columns.map((columnsData, childIndex) => {
                    const {
                      accessor,
                      width,
                      fallback,
                      align = 'start',
                      headerPostFix = '',
                      isReactComponent = false,
                    }=columnsData

                    if (rowData.label === 'Prop 65 Warning') {
                      rowData[`${accessor}`] = formatProp65Warning(
                        rowData[`${accessor}`],
                      )
                    }
                    const label = rowData[`${accessor}`] || fallback || ''
                    const mapKey =`${accessor}${index}${childIndex}`

                    return (
                      <Box
                        key={mapKey}
                        width={width || `${100 / columns.length}%`}
                        margin="xsmall"
                        a11yTitle={label}
                      >
                        {isLoading
                          ? <Loader />
                          : (
                            <LabelToDisplay
                              isReactComponent={isReactComponent}
                              label={label}
                              align={align}
                              textSize={textSize}
                              headerPostFix={headerPostFix}
                            />
                          )}
                      </Box>
                    )
                  })}
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      </Box>
    </>
  )
}

Table.propTypes = {
  data: PropTypes.array.isRequired,
  columns: PropTypes.array.isRequired,
  headerDirection: PropTypes.oneOf(['row', 'column']),
  showHeader: PropTypes.bool,
  titletype: PropTypes.oneOf(['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'text']),
  search: PropTypes.bool,
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  searchWidth: PropTypes.string,
  searchAction: PropTypes.func,
  border: PropTypes.bool,
  alternateColor: PropTypes.bool,
  textSize: PropTypes.oneOf(['small', 'medium', 'large']),
  margin: PropTypes.oneOf(['0px', 'small', 'medium', 'large']),
  isLoading: PropTypes.bool,
}
